import { cx } from '@lastbrand/web-ui-styled-system/css'
import { Fragment, useId, useMemo } from 'react'

import Product from '@src/quince-site-common/src/domains/Product'
import ProductOptionValue, {
  ProductOptionDisplayType,
  ProductOptionName,
} from '@src/quince-site-common/src/domains/ProductOption'
import { IProductPurchaseInfo } from '@src/quince-site-common/src/domains/ProductPurchaseInfo'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import { B2BSelfServeSizeQuantityGrid } from '@src/quince-site-common/src/organisms/b2b/b2b-size-quantity-grid'
import { areAllColorVariantsOnSale } from '@src/quince-site-common/src/utils/festivalOffersUtility'
import DeprecatedInputOption from '@src/quince-site-pdp/src/atoms/optionSelector/inputOption/DeprecatedInputOption'
import { OptionProp } from '@src/quince-site-pdp/src/atoms/optionSelector/OptionSelector.constant'
import { useMiniPdpOptionList } from '@src/quince-site-pdp/src/atoms/optionSelector/useMiniPdpOptionList'
import { useGetOutOfStockLastChanceSizes } from '@src/quince-site-pdp/src/hooks/useGetOutOfStockLastChanceSizes'
import { partition } from '@src/utils/functions/utils'

import { ColorGroupDS, type ColorOptionDSItem } from './color-option-ds'
import ImageOptionDSWithOptionValue from './image-option-ds'
import InputOptionDSWithOptionValue from './input-option-ds'
import {
  engravingOptionWrapperClass,
  excessElementsClass,
  excessElementsInteractiveClass,
  optionListGapClass,
  optionListGapPdpClass,
  optionListWrapperClass,
  optionNameClass,
  optionWrapperClass,
  packOptionWrapperClass,
} from './option-selector-ds.styles'
import PackOptionDSWithOptionValue from './pack-option-ds'
import TextOptionDSWithOptionValue from './text-option-ds'

// Non-COLOR renderer map (COLOR routes through `ColorGroupDS`). DEPRECATED_INPUT (the legacy
// single-char Customization input) has no DS equivalent — InputOptionDS is the free-text Engraving
// field — so it reuses the legacy `DeprecatedInputOption`, which only reads `onChange` off `OptionProp`.
const DS_DISPLAY_TYPE_VS_OPTION_RENDERER: Partial<
  Record<ProductOptionDisplayType, (props: OptionProp) => JSX.Element>
> = {
  [ProductOptionDisplayType.TEXT]: TextOptionDSWithOptionValue,
  [ProductOptionDisplayType.IMAGE]: ImageOptionDSWithOptionValue,
  [ProductOptionDisplayType.INPUT]: InputOptionDSWithOptionValue,
  [ProductOptionDisplayType.DEPRECATED_INPUT]: DeprecatedInputOption,
  [ProductOptionDisplayType.PACK]: PackOptionDSWithOptionValue,
}

const isDisplayTypeSupported = (displayType: ProductOptionDisplayType): boolean =>
  displayType === ProductOptionDisplayType.COLOR || !!DS_DISPLAY_TYPE_VS_OPTION_RENDERER[displayType]

// Both `onSelect` (legacy) and `onOptionSelect` (DS) are accepted; see `handleSelect`.
export interface OptionSelectorDSProps {
  className?: string
  optionDisplayTypesVsClassName?: Record<string, string | undefined>
  optionValueList: ProductOptionValue[]
  selectedValue: string | undefined
  onSelect?: (value: string) => void
  onOptionSelect?: (value: string) => void
  isAllRoundOptions?: boolean
  showSelectedName?: boolean
  classNameOption?: string
  product?: Product
  selectedOptions?: ProductVariantOption[]
  optionWrapperClassName?: string
  selectorKey?: string
  isPDP?: boolean
  showMoreOption?: boolean
  optionLimit?: number
  isMiniPDP?: boolean
  onShowMoreClick?: () => void
  renderAfterColorComponent?: () => React.ReactNode
  displayName?: string
  excessOptionsTextClassName?: string
  productPurchaseInfo?: IProductPurchaseInfo
  inputOptionError?: string
  isBundle?: boolean
  optionName?: string
  isB2BSelfService?: boolean
}

const OptionSelectorDS = ({
  isAllRoundOptions = true,
  showMoreOption,
  optionLimit,
  isMiniPDP,
  onShowMoreClick,
  renderAfterColorComponent,
  displayName,
  excessOptionsTextClassName,
  productPurchaseInfo,
  product,
  selectedOptions,
  isBundle,
  optionName,
  onSelect,
  onOptionSelect,
  ...props
}: OptionSelectorDSProps) => {
  const { optionValueList, isB2BSelfService } = props
  const selectionSetId = useId()

  // PDP/QATC uses the legacy V2 option-row gaps; product cards keep the default rhythm.
  const optionGapClass = props.isPDP ? optionListGapPdpClass : optionListGapClass

  // `onOptionSelect` (DS naming) wins if provided, else legacy `onSelect`. Exactly one fires so a caller
  // wiring both names can't double-count the variant-select analytics event.
  const handleSelect = (value: string): void => {
    ;(onOptionSelect ?? onSelect)?.(value)
  }

  const validOptionValueList = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- callers can pass undefined despite the type
      (optionValueList || [])
        .filter(optionValue => isDisplayTypeSupported(optionValue.displayType))
        .filter(
          optionValue =>
            !!(optionValue.hexCode ?? (optionValue.displayType !== ProductOptionDisplayType.TEXT || props.isPDP)),
        ),
    [optionValueList, props.isPDP],
  )

  const lastChanceSizes = useGetOutOfStockLastChanceSizes({
    product,
    displayName,
    productPurchaseInfo,
    selectedOptions,
  })

  const getMiniPdpOptionList = useMiniPdpOptionList()

  const { filteredOptionValueList, noOfExcessElements } = useMemo(() => {
    if (isMiniPDP) {
      return getMiniPdpOptionList({
        validOptionValueList,
        selectedValue: props.selectedValue,
        optionLimit: optionLimit ?? 0,
        showMoreOption,
      })
    }

    const excess = Math.max(validOptionValueList.length - (optionLimit ?? 0), 0)

    if (!showMoreOption || !excess) {
      return { filteredOptionValueList: validOptionValueList, noOfExcessElements: 0 }
    }

    const [selectedOptionArray, unSelectedOptionArray] = partition(
      validOptionValueList,
      optionValue => optionValue.value === props.selectedValue,
    )

    const numberOfSelectedOptions = selectedOptionArray.length

    const visibleOptionsArray = [
      ...selectedOptionArray,
      ...unSelectedOptionArray.slice(0, (optionLimit ?? 0) - numberOfSelectedOptions),
    ]

    return { filteredOptionValueList: visibleOptionsArray, noOfExcessElements: excess }
  }, [validOptionValueList, showMoreOption, optionLimit, isMiniPDP, getMiniPdpOptionList, props.selectedValue])

  if (isB2BSelfService && optionName === ProductOptionName.Size) {
    return (
      <B2BSelfServeSizeQuantityGrid
        optionValueList={filteredOptionValueList}
        product={product}
        selectedOptions={selectedOptions}
        productPurchaseInfo={productPurchaseInfo}
      />
    )
  }

  // COLOR group renders through one SelectColorGroup Root; the `+N` overflow is rendered externally below.
  // A color group can mix COLOR swatches with IMAGE pattern swatches (e.g. a stripe). ColorGroupDS renders
  // both (hexColor or imageUrl), so route the whole group through it whenever it holds a color swatch — the
  // map path below has no COLOR renderer, so a single IMAGE entry would otherwise drop every COLOR option.
  const isColorGroup =
    filteredOptionValueList.length > 0 &&
    filteredOptionValueList.some(o => o.displayType === ProductOptionDisplayType.COLOR) &&
    filteredOptionValueList.every(
      o => o.displayType === ProductOptionDisplayType.COLOR || o.displayType === ProductOptionDisplayType.IMAGE,
    )

  // `+N` excess control. The ARIA `role` is a static token and must NOT be passed
  // through `t()` — a translated value emits an invalid role in non-English locales
  // (WCAG 4.1.2). Use the literal `"button"` token; only user-facing copy is translated.
  const excessControl =
    noOfExcessElements > 0 ? (
      <div
        className={cx(
          excessElementsClass,
          onShowMoreClick && excessElementsInteractiveClass,
          excessOptionsTextClassName,
        )}
        // Interactive only when there's a handler — without one it'd be a focusable no-op (dead-end).
        {...(onShowMoreClick && {
          onClick: onShowMoreClick,
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onShowMoreClick()
            }
          },
          role: 'button',
          tabIndex: 0,
        })}
      >
        +{noOfExcessElements}
      </div>
    ) : null

  if (isColorGroup) {
    const items: ColorOptionDSItem[] = filteredOptionValueList.map((optionValue, index) => ({
      optionValue,
      index,
      showRedDot: Boolean(props.isPDP && areAllColorVariantsOnSale({ product, optionValue, optionName }) && !isBundle),
    }))

    return (
      <>
        <div className={cx(props.className, optionListWrapperClass, optionGapClass)}>
          <ColorGroupDS
            items={items}
            selectedValue={props.selectedValue}
            onOptionSelect={handleSelect}
            selectionSetId={selectionSetId}
            selectorKey={props.selectorKey}
            optionName={optionName}
            ariaLabel={displayName ?? optionName}
          />
          {excessControl}
        </div>
        {renderAfterColorComponent?.()}
      </>
    )
  }

  let salePrice = 0

  return (
    <>
      <div
        className={cx(props.className, optionListWrapperClass, optionGapClass)}
        data-content-type="option-selector"
        data-option-type={optionName}
      >
        {filteredOptionValueList.map((optionValue, index) => {
          const OptionRenderer = DS_DISPLAY_TYPE_VS_OPTION_RENDERER[optionValue.displayType]
          if (!OptionRenderer) return null

          if (optionValue.displayType === ProductOptionDisplayType.PACK && product) {
            const updatedSelectedOptions: ProductVariantOption[] =
              productHelper.getUpdatedSelectedOptionsOnProductOptionSelect(
                product,
                selectedOptions ?? [],
                ProductOptionName.Size,
                optionValue.value,
              )
            salePrice = productHelper.getQuincePriceForOptions(product, updatedSelectedOptions)
          }
          const inputOptionError =
            optionValue.displayType === ProductOptionDisplayType.INPUT ? props.inputOptionError : undefined

          const shouldShowRedDot =
            props.isPDP && areAllColorVariantsOnSale({ product, optionValue, optionName }) && !isBundle

          return (
            <Fragment key={optionValue.value}>
              <div
                data-content-type="option-wrapper"
                data-value={optionValue.value}
                data-is-selected={optionValue.value === props.selectedValue}
                data-selector-key={index === 0 ? props.selectorKey : undefined}
                className={cx(
                  optionWrapperClass,
                  optionValue.displayType === ProductOptionDisplayType.PACK && packOptionWrapperClass,
                  optionValue.displayType === ProductOptionDisplayType.INPUT && engravingOptionWrapperClass,
                  props.optionWrapperClassName,
                )}
              >
                <OptionRenderer
                  className={cx(props.optionDisplayTypesVsClassName?.[optionValue.displayType], props.classNameOption)}
                  selectionSetId={selectionSetId}
                  optionValue={optionValue}
                  isSelected={optionValue.value === props.selectedValue}
                  addborder={optionValue.addBorder}
                  isRound={isAllRoundOptions}
                  onChange={handleSelect}
                  salePrice={salePrice}
                  maxCharacterLimit={optionValue.maxCharacterLimit}
                  minCharacterLimit={optionValue.minCharacterLimit}
                  lastChanceSizes={lastChanceSizes}
                  inputOptionError={inputOptionError}
                  showRedDot={shouldShowRedDot}
                />
              </div>
              {props.showSelectedName && optionValue.value === props.selectedValue && (
                <span className={optionNameClass}>{optionValue.value}</span>
              )}
            </Fragment>
          )
        })}
        {excessControl}
      </div>
      {renderAfterColorComponent?.()}
    </>
  )
}

export default OptionSelectorDS
